import { useEffect, useRef } from "react";

interface DataActionsProps {
  surfaceData: google.maps.Data;
  onFeatureClick: (event: google.maps.Data.MouseEvent) => void;
  onFeatureSetGeometry: (event: google.maps.Data.SetGeometryEvent) => void;
  onFeatureRightClick: (event: google.maps.Data.MouseEvent) => void;
}

/**
 * DataActions component handles event listeners for Google Maps Data layer interactions
 */
export default function DataActions({
  surfaceData,
  onFeatureClick,
  onFeatureSetGeometry,
  onFeatureRightClick,
}: DataActionsProps) {
  // Use refs to store the latest event handlers without causing re-renders
  const handlersRef = useRef<{
    onFeatureClick: (event: google.maps.Data.MouseEvent) => void;
    onFeatureSetGeometry: (event: google.maps.Data.SetGeometryEvent) => void;
    onFeatureRightClick: (event: google.maps.Data.MouseEvent) => void;
  }>({
    onFeatureClick,
    onFeatureSetGeometry,
    onFeatureRightClick,
  });
  handlersRef.current = {
    onFeatureClick,
    onFeatureSetGeometry,
    onFeatureRightClick,
  };

  // Debounce timer ref for setgeometry events
  const debounceTimerRef = useRef<number | null>(null);

  // Add event listeners to the surface data layer
  useEffect(() => {
    if (!surfaceData) return;

    const listeners: google.maps.MapsEventListener[] = [];

    // Create stable wrapper functions that use the latest handlers from the ref
    const clickListener = surfaceData.addListener(
      "click",
      (event: google.maps.Data.MouseEvent) => {
        handlersRef.current.onFeatureClick(event);
      },
    );
    listeners.push(clickListener);

    const setGeometryListener = surfaceData.addListener(
      "setgeometry",
      (event: google.maps.Data.SetGeometryEvent) => {
        // Clear any existing debounce timer
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }

        // Set a new debounce timer - only call the handler after drag has stopped
        debounceTimerRef.current = window.setTimeout(() => {
          handlersRef.current.onFeatureSetGeometry(event);
          debounceTimerRef.current = null;
        }, 150); // 150ms delay - adjust as needed
      },
    );
    listeners.push(setGeometryListener);

    const rightClickListener = surfaceData.addListener(
      "contextmenu",
      (event: google.maps.Data.MouseEvent) => {
        handlersRef.current.onFeatureRightClick(event);
      },
    );
    listeners.push(rightClickListener);

    // Cleanup event listeners when surfaceData changes
    return () => {
      // Clear any pending debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }

      listeners.forEach((listener) => {
        google.maps.event.removeListener(listener);
      });
    };
  }, [surfaceData]); // Only depend on surfaceData

  // This component doesn't render anything - it just manages event listeners
  return null;
}
